import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { startRegistration } from '@simplewebauthn/browser';
import {
  getFormattedDate,
  getTimeZoneValuesWithDiff,
} from '../../../../global.variable';
import { AppService } from '../../../app.service';
import { UserProfileService } from '../../../profile/user-profile/user-profile.service';
import { DialogRef } from '../../../shared/components/modal-dialogue/modal-dialogue.ref';
import { DataCheckService } from '../../../shared/services/data-check.service';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner.service';
import { ToasterService } from '../../../shared/services/toaster.service';
import { MustMatch } from '../../../shared/validators/must-match-validator';
import { UsersService } from '../../../pages/pages/users/users.service';

@Component({
  selector: 'gtapp-app-checks',
  templateUrl: './app-checks.component.html',
  styleUrl: './app-checks.component.scss',
  standalone: false,
})
export class AppChecksComponent implements OnInit {
  today = getFormattedDate(new Date());
  passwordForm: UntypedFormGroup = new UntypedFormGroup(
    {
      password: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(8),
      ]),
      confirm_password: new UntypedFormControl(null, [
        MustMatch('password', 'confirm_password'),
      ]),
    },
    {
      validators: MustMatch('password', 'confirm_password'),
    },
  );

  profileForm: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl(''),
    first_name: new UntypedFormControl('', Validators.required),
    last_name: new UntypedFormControl('', Validators.required),
    mobile_number: new UntypedFormControl(null),
    time_zone: new UntypedFormControl(''),
  });
  identificationForm: UntypedFormGroup = new UntypedFormGroup({
    valid_till: new UntypedFormControl(getFormattedDate(new Date()), [
      Validators.required,
    ]),

    issuer_state: new UntypedFormControl(''),
    licence_class: new UntypedFormControl(null),
    id_number: new UntypedFormControl(null, [Validators.required]),
    id_type: new UntypedFormControl('SECURITY LICENCE', [Validators.required]),
    photos: new UntypedFormControl([]),
    issue_date: new UntypedFormControl(null),
  });

  userData: any;
  isSuperUser: boolean = this.dataCheckService.isSuperUser();

  currentStatus: string = 'passwordSet';
  showPassword: boolean = false;

  addCancelButton: boolean = false;
  currentStep: number = 1;

  stateTimeZones: any = [];

  userSignUp: boolean = false;

  fidoError: any;

  isPrimeContractorUser = this.dataCheckService.isPrimeContractorUser();

  constructor(
    private appService: AppService,
    private dialogRef: DialogRef,
    private profileService: UserProfileService,
    private userService: UsersService,
    private dataCheckService: DataCheckService,
    private spinnerService: LoadingSpinnerService,
    private toasterService: ToasterService,
    private sanitizer: DomSanitizer,
  ) {
    this.userData = this.appService.getUserData();
    this.getCurrentStatus();
  }
  getCurrentStatus() {
    this.spinnerService.show();
    if (this.dataCheckService.hasPasswordSet() === true) {
      this.currentStatus = 'passwordSet';
      this.addCancelButton = true;
      this.spinnerService.hide();
    } else {
      this.currentStatus = 'profileDetails';
      this.userSignUp = true;
      this.getStates();
      this.spinnerService.hide();
    }
  }
  onCloseDialogue() {
    this.dialogRef.close();
    window.localStorage.removeItem('resetPasswordPWA');
  }

  ngOnInit(): void {
    if (
      this.userData?.profile?.first_name ||
      this.userData?.profile?.last_name
    ) {
      this.profileForm.controls['first_name'].setValue(
        this.userData.profile.first_name,
      );
      this.profileForm.controls['last_name'].setValue(
        this.userData.profile.last_name,
      );
    }
    this.profileForm.controls['time_zone'].setValue(
      this.userData.profile.time_zone,
    );
  }

  getStates() {
    this.appService.getStates(1, {}).subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.stateTimeZones = getTimeZoneValuesWithDiff(
          response?.time_zone_list,
        );
      }
    });
  }

  updateProfileData() {
    if (!this.profileForm.valid) {
      this.toasterService.setMessage({
        errorMessage: 'Invalid data',
        successMessage: '',
      });
      return;
    }
    if (!this.profileForm.value?.mobile_number) {
      this.profileForm.controls['mobile_number'].setValue(null);
    }

    this.profileForm.controls['email'].setValue(this.userData.profile.email);
    let requestData = { data: this.profileForm.value };

    if (this.isSuperUser === true) {
      this.userService
        .editSuperAdmin(requestData, this.userData?.profile?.id)
        .subscribe((response: any) => {
          this.toasterService.setMessage({
            successMessage: 'Profile Updated',
            errorMessage: '',
          });

          if (this.dataCheckService.hasPasswordSet() === false) {
            this.currentStatus = 'passwordSet';
          } else {
            this.dialogRef.close('close');
          }

          this.userData.profile = response;
          this.appService.setUserData(this.userData);
        });
    } else {
      this.spinnerService.show('Updating Info');
      this.profileService
        .updateProfileDetails(requestData)
        .then(async (response: any) => {
          if (response['status'] == 'success') {
            if (this.identificationForm.valid) {
              this.addUserCredential();
            }
            this.toasterService.setMessage({
              errorMessage: '',
              successMessage: response['message'],
            });
            if (this.dataCheckService.hasPasswordSet() === false) {
              this.currentStatus = 'passwordSet';
            } else {
              this.dialogRef.close('close');
            }

            this.userData.profile = response['data'];
            this.appService.setUserData(this.userData);
            this.spinnerService.hide();
          } else {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response['message'],
            });
          }
        });
    }
  }
  changePassword() {
    if (this.isSuperUser === true) {
      this.profileService
        .changeAdminPassword(this.passwordForm.value)
        .subscribe((response) => {
          if (response['status'] == 'success') {
            let userData = this.appService.getUserData();
            userData.user_token = response.user_token;
            userData.checks.password_set = true;

            this.appService.setUserData(userData);
            this.dialogRef.close('close');
          }
        });
    } else {
      this.profileService
        .changePassword(this.passwordForm.value)
        .subscribe((response) => {
          if (response['status'] == 'success') {
            this.toasterService.setMessage({
              errorMessage: '',
              successMessage: response['message'],
            });
            let userData = this.appService.getUserData();
            userData.user_token = response.user_token;
            userData.checks.password_set = true;

            localStorage.removeItem('resetPasswordPWA');
            this.appService.setUserData(userData);
            const isPrimeContractorUser =
              this.dataCheckService.isPrimeContractorUser(); // https://app.shortcut.com/sendem/story/13759/temporarily-remove-passkey-functionality-for-prime-contractors
            if (this.userSignUp && !isPrimeContractorUser) {
              this.currentStatus = 'webauthn';
            } else {
              this.dialogRef.close('close');
            }
          } else {
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response['message'],
            });
          }
        });
    }
  }
  getInputType() {
    if (this.showPassword) {
      return 'text';
    }
    return 'password';
  }
  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  async fidoStartRegistration(responseBody: any) {
    try {
      let regResp = await startRegistration({
        optionsJSON: JSON.parse(responseBody),
      });
      if (regResp) {
        this.profileService
          .fidoRegister(regResp, { verify: 1 })
          .then((response: any) => {
            if (response['status'] == 'success') {
              this.currentStatus = 'webauthnSuccess';
              // window.location.reload();
              this.spinnerService.hide();
            } else {
              this.spinnerService.hide();
              this.getErrorMessage(response['message']);
              this.toasterService.setMessage({
                successMessage: '',
                errorMessage: response['message'],
              });
            }
          });
      }
    } catch (err: any) {
      this.spinnerService.hide();
      if (err?.message === 'The authenticator was previously registered') {
        localStorage.setItem(btoa('fidoConfiguredInDevice'), 'true');
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: 'The authenticator was previously registered',
        });
      }
      if (err?.message) {
        this.spinnerService.hide();
        this.getErrorMessage(err?.message);
      }
    }
  }

  fidoRegister() {
    this.spinnerService.show();
    this.profileService.fidoRegister().then((response: any) => {
      if (response['status'] == 'success') {
        this.fidoStartRegistration(response['data']);
      } else {
        this.spinnerService.hide();
        this.getErrorMessage(response['message']);
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response['message'],
        });
      }
    });
  }
  getErrorMessage(errorMsg: any) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const formattedMessage = String(errorMsg)?.replace(
      urlRegex,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
    );
    this.fidoError = this.sanitizer.bypassSecurityTrustHtml(formattedMessage);
  }
  addUserCredential() {
    if (this.identificationForm.value.valid_till)
      this.identificationForm.value.valid_till = formatDate(
        this.identificationForm.value.valid_till,
        'yyyy-MM-dd',
        'en',
      );
    let fileData: FormData = new FormData();

    Object.entries(this.identificationForm.value).forEach(([key, value]) => {
      if (value) {
        if (key == 'photos') {
          if (value as any) {
            const uploadFiles: any = value || [];
            for (const item of uploadFiles) {
              const file: any = item;
              if (file?.file) {
                fileData.append(file?.file_number, file?.file);
              }
            }
          }
        } else {
          fileData.append(key, value as any);
        }
      }
    });
    try {
      this.appService
        .formDataApi(`credential`, fileData, false, {
          credential_owner: 'user',
          from_manage_cred_page: 0,
        })
        .then((response: any) => {});
    } catch (error) {}
  }
}
